// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\components\Siniestros\FiniquitoChubbTemplate.js
import PropTypes from "prop-types";

const MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre"
];

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Acepta Date | ISO | "YYYY-MM-DD" | "DD/MM/YYYY"
 * Devuelve: "DD de <mes> de YYYY"
 */
function formatDateLongES(input) {
  if (!input) return "";

  let d = null;

  if (input instanceof Date) {
    d = input;
  } else if (typeof input === "string") {
    const s = input.trim();

    // "DD/MM/YYYY"
    const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m1) {
      const dd = Number(m1[1]);
      const mm = Number(m1[2]); // 1..12
      const yyyy = Number(m1[3]);
      d = new Date(yyyy, mm - 1, dd);
    } else {
      // ISO o "YYYY-MM-DD" o algo parseable por Date
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) d = dt;
    }
  }

  if (!d || isNaN(d.getTime())) return String(input);

  const dd = pad2(d.getDate());
  const mmName = MONTHS_ES[d.getMonth()];
  const yyyy = d.getFullYear();

  return `${dd} de ${mmName} de ${yyyy}`;
}

/**
 * Saca el año (YYYY) desde:
 * - Date
 * - "DD/MM/YYYY"
 * - "YYYY-MM-DD" / ISO
 * Si no puede, usa el año actual.
 */
function getYearFrom(input) {
  if (!input) return new Date().getFullYear();

  if (input instanceof Date) {
    const y = input.getFullYear();
    return Number.isFinite(y) ? y : new Date().getFullYear();
  }

  if (typeof input === "string") {
    const s = input.trim();

    // "DD/MM/YYYY"
    const m1 = s.match(/^\d{2}\/\d{2}\/(\d{4})$/);
    if (m1) return Number(m1[1]);

    // "YYYY-MM-DD" o ISO
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) return dt.getFullYear();
  }

  return new Date().getFullYear();
}

export default function FiniquitoChubbTemplate({ data }) {
  const {
    fechaHoy,
    refFast,
    refAlbaran,
    aseguradoNombre,
    propiedadFranquicia,
    fechaSiniestro,
    importeNetoCobrar,
    cuenta
  } = data || {};

  const red = { color: "#cc0000" };
  const bold = { fontWeight: "bold" };
  const linkBlue = { color: "#0000EE", textDecoration: "underline" }; // azul típico link

  // Año automático: prioriza fechaHoy (la de la carta). Si no, usa año actual.
  const yearFactura = getYearFrom(fechaHoy);
  const VARIANTES = {
    SO:   { ramo: "Transportes SO",   poliza: "ESCGN245001" },
    YTLL: { ramo: "Transportes YTLL", poliza: "ESCGN245000" },
  };
  const variante = VARIANTES[String(data?.tipoPago || "").trim().toUpperCase()]
    ?? { ramo: "Transportes", poliza: "ESCGN244946" };

  // Reservamos sitio para el footer fijo abajo (IMPORTANTE)
  // const footerHeightMm = 0;

  const page = {
    width: "210mm",
    height: "297mm",
    boxSizing: "border-box",
    background: "#fff",
    color: "#000",
    border: "1px solid #000",
    position: "relative",
    overflow: "hidden",
    fontFamily: "Times New Roman, Times, serif",
    fontSize: "10pt",
    lineHeight: "1.15",
    padding: "16mm 18mm 14mm 18mm"
    // paddingBottom: `calc(14mm + ${footerHeightMm}mm)` // deja hueco para el footer
  };

  const p = { textAlign: "justify", margin: 0 };
  const space = (mm) => <div style={{ height: `${mm}mm` }} />;

  // Hueco para logo
  const leftGutterMm = 34;
  const chubbTopMm = 75;

  // Footer legal (fixed al borde inferior)
  const footerStyle = {
    // position: "absolute",
    left: "18mm",
    right: "18mm",
    bottom: "3mm", // más pegado al borde inferior
    fontSize: "7pt", // un poco más grande
    lineHeight: "1.1",
    textAlign: "justify"
  };

  return (
    <div style={page}>
      {/* Fecha (arriba derecha en rojo) */}
      <div
        style={{
          position: "absolute",
          top: "10mm",
          right: "18mm",
          fontSize: "10pt",
          ...red,
          ...bold
        }}
      >
        {formatDateLongES(fechaHoy)}
      </div>

      {/* Dirección arriba: MISMA POSICIÓN, pero alineado a la derecha */}
      <div style={{ textAlign: "center", fontSize: "10pt" }}>
        <div style={{ display: "inline-block", textAlign: "right" }}>
          <div style={bold}>Chubb European Group SE</div>
          <div>Paseo de la Castellana 14, Planta 6</div>
          <div>28046 Madrid</div>
          <div>España</div>
        </div>
      </div>

      {space(10)}

      {/* ===== BLOQUE PRINCIPAL CON 2 COLUMNAS ===== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${leftGutterMm}mm 1fr`,
          columnGap: "6mm",
          alignItems: "start"
        }}
      >
        {/* Columna izquierda (hueco + logo) */}
        <div style={{ position: "relative", minHeight: "170mm" }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: `${chubbTopMm}mm`,
              fontFamily: "Arial, sans-serif",
              letterSpacing: "3px",
              fontSize: "16pt",
              color: "#d1008f"
            }}
          >
            CHUBB
          </div>
        </div>

        {/* Columna derecha (todo el texto) */}
        <div>
          {/* Bloque datos */}
          <div style={{ fontSize: "10pt" }}>
            <div style={bold}>Ramo: {variante.ramo}</div>

            <div style={bold}>
              Expediente: <span style={red}>{refFast}</span>
            </div>

            <div style={bold}>
              Nº referencia cliente: <span style={red}>{refAlbaran}</span>
            </div>

            <div style={bold}>Póliza: {variante.poliza}</div>

            <div style={bold}>
              Asegurado: <span style={red}>{aseguradoNombre}</span>
            </div>
          </div>

          {space(10)}

          {/* Título centrado + subrayado */}
          <div
            style={{
              textAlign: "center",
              ...bold,
              fontSize: "10pt",
              textDecoration: "underline"
            }}
          >
            Recibo Finiquito
          </div>

          {space(6)}

          {/* Párrafos */}
          <p style={{ ...p, marginBottom: "6mm" }}>
            <span style={red}>{propiedadFranquicia}</span> en méritos de lo convenido y como consecuencia del siniestro
            ocurrido el día <span style={red}>{fechaSiniestro}</span>, reconoce(n) haber recibido de la Compañía
            Aseguradora Chubb European Group SE, Sucursal en España la cantidad de{" "}
            <span style={red}>{importeNetoCobrar}</span> como pago final y definitivo en concepto de indemnización por el
            siniestro objeto de este documento.
          </p>

          <p style={{ ...p, marginBottom: "6mm" }}>
            Mediante el cobro de esta cantidad, la cual damos fe de haber percibido, doy/damos a Chubb European Group SE,
            Sucursal en España por libre y descargada por el presente y el futuro de toda obligación resultante de este
            siniestro y renuncio/renunciamos a toda reclamación o acción, cualesquiera que sean las consecuencias
            actuales o eventuales de dicho siniestro.
          </p>

          <p style={{ ...p, marginBottom: "8mm" }}>
            En consideración al referido pago, se entiende que Chubb European Group SE, Sucursal en España queda
            subrogada (hasta el límite que señalan las leyes) en todos nuestros derechos y recursos, y queda autorizada
            para hacer uso de nuestro nombre con el propósito de llevar a cabo cualquier acto o medida legal apropiados
            con fines de ejecución de tales derechos o recursos y, en consecuencia, nos comprometemos a facilitar, a
            requerimiento de Chubb European Group SE, Sucursal en España la documentación obrante en nuestro poder en
            relación con el siniestro de referencia, así como a prestar a Chubb European Group SE, Sucursal en España,
            el apoyo o la asistencia que pudiera necesitar en la ejecución de las reclamaciones que emprenda frente a
            terceros.
          </p>

          <div style={{ fontSize: "10pt", marginBottom: "5mm" }}>
            En ....................................................., a .................. de .......................... de{" "}
            {yearFactura}.
          </div>

          <div style={{ fontSize: "10pt" }}>Por recibo y finiquito,</div>
          <div style={{ fontSize: "10pt", marginBottom: "14mm" }}>El asegurado y/o Entidad Asegurada</div>

          <div style={{ fontSize: "10pt", marginBottom: "4mm" }}>Firma y sello del Asegurado</div>

          {/* Tabla Swift/IBAN (control por padding: estable con html2canvas) */}
          {(() => {
            // Ajustes finos (toca SOLO estos si hace falta)
            const HEADER_H = 28; // alto fila verde
            const HEADER_PT = 1; // sube/baja Swift/IBAN (0..2 típico)
            const VALUE_H = 28; // alto fila valor
            const VALUE_PT = 1; // sube/baja el IBAN rojo (0..3 típico)

            return (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                  marginBottom: "5mm",
                  fontSize: "10.5pt"
                }}
              >
                <tbody>
                  {/* Fila verde */}
                  <tr>
                    <td
                      style={{
                        width: "22%",
                        border: "1px solid #000",
                        background: "#7DBB00",
                        color: "#fff",
                        fontWeight: "bold",
                        height: `${HEADER_H}px`,
                        padding: `${HEADER_PT}px 8px 0 8px`,
                        verticalAlign: "top",
                        lineHeight: "1",
                        textAlign: "left"
                      }}
                    >
                      Swift:
                    </td>

                    <td
                      style={{
                        width: "78%",
                        border: "1px solid #000",
                        background: "#7DBB00",
                        color: "#fff",
                        fontWeight: "bold",
                        height: `${HEADER_H}px`,
                        padding: `${HEADER_PT}px 8px 0 8px`,
                        verticalAlign: "top",
                        lineHeight: "1",
                        textAlign: "left"
                      }}
                    >
                      IBAN:
                    </td>
                  </tr>

                  {/* Fila datos */}
                  <tr>
                    <td
                      style={{
                        border: "1px solid #000",
                        height: `${VALUE_H}px`,
                        padding: "0 8px",
                        verticalAlign: "top"
                      }}
                    />

                    <td
                      style={{
                        border: "1px solid #000",
                        height: `${VALUE_H}px`,
                        padding: `${VALUE_PT}px 8px 0 8px`,
                        verticalAlign: "top",
                        lineHeight: "1",
                        textAlign: "left"
                      }}
                    >
                      <span style={{ color: "#cc0000" }}>{cuenta}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            );
          })()}

          {/* Subido y un poco más grande (más pegado a la tabla) */}
          <div style={{ fontSize: "10pt", marginTop: "-2mm", marginBottom: "2mm" }}>
            Este finiquito tendrá validez, una vez realizada transferencia bancaria por el citado importe a su cuenta
            bancaria.
          </div>
        </div>
      </div>

      {/* Footer legal (links azules + tamaño algo mayor y más arriba) */}
      <div style={footerStyle}>
        Usamos los datos personales que nos proporciona (o que, en su caso, usted proporciona a su corredor de seguros)
        para los fines de suscripción, administración y gestión de pólizas, gestión de siniestros y otros fines
        aseguradores, tal y como se describen en mayor detalle en nuestra Política de Privacidad, que se encuentra
        disponible a través del vínculo siguiente:{" "}
        <span style={linkBlue}>https://www2.chubb.com/es-es/footer/privacy-policy.aspx</span>. En cualquier momento, puede
        usted solicitarnos una copia en papel de la misma simplemente contactándonos a través del correo electrónico:{" "}
        <span style={linkBlue}>dataprotectionoffice.europe@chubb.com</span>. Chubb European Group SE is an undertaking
        governed by the provisions of the French insurance code with registration number 450 327 374 RCS Nanterre and the
        following registered office: La Tour Carpe Diem, 31 Place des Corolles, Esplanade Nord, 92400 Courbevoie, France.
        Chubb European Group SE has fully paid share capital of €896,176,662. UK business address: 100 Leadenhall Street,
        London EC3A 3BP. Supervised by the Autorité de contrôle prudentiel et de résolution (ACPR) 4, Place de Budapest,
        CS 92459, 75436 PARIS CEDEX 09 and subject to limited regulation by the Financial Conduct Authority. Details
        about the extent of our regulation by the Financial Conduct Authority are available from us on request. You can
        find details about the firm by searching ‘Chubb European Group SE’ online at{" "}
        <span style={linkBlue}>https://register.fca.org.uk</span>
      </div>
    </div>
  );
}

FiniquitoChubbTemplate.propTypes = {
  data: PropTypes.shape({
    tipoPago: PropTypes.string,
    fechaHoy: PropTypes.string,
    refFast: PropTypes.string,
    refAlbaran: PropTypes.string,
    aseguradoNombre: PropTypes.string,
    propiedadFranquicia: PropTypes.string,
    fechaSiniestro: PropTypes.string,
    importeNetoCobrar: PropTypes.string,
    cuenta: PropTypes.string
  })
};
